import { FC, useState } from "react";
import { FileViewer, styled } from "@monolith-forensics/monolith-ui";
import { FileIcon } from "lucide-react";

import Loader from "@/components/Loader";
import { convertBytesToSize } from "@/utils/convertBytesToSize";
import { monolithMoment } from "@/utils/date-format";
import type { AssetAttachment } from "@/Features/AssetAttachments/asset-attachments.types";
import { useValidationReports } from "@/Features/AssetAttachments/asset-attachments.query";
import { FileList, FileRow } from "./FileRow";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0px;
`;

const SectionHeader = styled.h3`
  margin: 0px;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

/**
 * The reports attached to one validation (ENG-1673), read-only. Each row
 * opens the file in mfui's FileViewer. `link` is passed through as signed
 * (no localhost:3000 → 3001 rewrite), the same as Case Files and Evidence
 * Photos do. The parent keys this on the validation uuid so the viewer
 * selection resets on Next/Previous.
 */
export const ValidationReports: FC<{
  assetUuid: string;
  validationUuid: string;
}> = ({ assetUuid, validationUuid }) => {
  const { data, isLoading, isError } = useValidationReports(
    assetUuid,
    validationUuid
  );
  const [selected, setSelected] = useState<AssetAttachment | null>(null);

  return (
    <Section aria-label="Reports">
      <SectionHeader>Reports</SectionHeader>
      {isLoading ? (
        <Loader />
      ) : isError && !data ? (
        <Muted>Couldn't load reports.</Muted>
      ) : !data?.length ? (
        <Muted>No reports</Muted>
      ) : (
        <FileList>
          {data.map((row) => (
            <li key={row.uuid}>
              <FileRow
                type="button"
                disabled={!row.link}
                onClick={() => setSelected(row)}
              >
                <FileIcon size={14} />
                <span className="file-name" title={row.name}>
                  {row.name}
                </span>
                {row.size != null && (
                  <span className="file-meta">
                    {convertBytesToSize(row.size)}
                  </span>
                )}
                {row.created_on && (
                  <span className="file-meta">
                    {monolithMoment({ timestamp: row.created_on })}
                  </span>
                )}
              </FileRow>
            </li>
          ))}
        </FileList>
      )}
      <FileViewer
        file={{
          name: selected?.name ?? "",
          url: selected?.link,
          ext: selected?.ext ?? undefined,
          size: selected?.size ?? undefined,
        }}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </Section>
  );
};
